import type React from 'react'
import { PORT_HIT_SIZE, type PortPoint } from '@/lib/text-linking/text-linking-geometry'

import { portControlAttrs } from '../handlers/port-control-attrs'
import type { PortControlProps } from '../types/port-control'
import { PortBadgeContainer } from './port-badge-container'
import { PortFocusRing } from './port-focus-ring'
import { PortSeqNumeral } from './port-seq-numeral'

interface PortSquareProps {
  center: PortPoint
  rotationDeg: number
  /** Chain sequence number to show centered in the square, if any. */
  seq?: number
  /** Passive ports (e.g. other chains shown via the View toggle) render decorative-only. */
  interactive: boolean
  cursor?: string
  onClick?: () => void
  /** Which edge this square sits on — purely a `data-port-role` label; defaults to the common
   *  (in-port) case since most callers are in-ports. */
  portRole?: 'in' | 'out'
  /** Present only when this port is a real control — see `PortControlProps`. */
  control?: PortControlProps
}

/**
 * A receiving (in) or passive sending (out) port square — a rounded square on a translucent halo,
 * with an optional centered chain-sequence number. `center` and `rotationDeg` are rotation-aware
 * (see `inPortPoint`/`outPortPoint`); the square itself is drawn at the local origin and the whole
 * group is translated + rotated so its edges stay flush with a rotated frame's edge. `center`
 * already arrives in screen pixels (`chromeFrameOf`), and every size here (`PORT_HIT_SIZE`, and
 * the shared badge tile's own 24 viewBox) is a literal screen measurement — no counter-scale to
 * apply or order against `rotate` any more.
 */
export function PortSquare({
  center,
  rotationDeg,
  seq,
  interactive,
  cursor = 'default',
  onClick,
  portRole = 'in',
  control,
}: PortSquareProps): React.JSX.Element {
  return (
    <g
      transform={`translate(${center.x}, ${center.y}) rotate(${rotationDeg})`}
      style={{ pointerEvents: interactive ? 'auto' : 'none' }}
      aria-hidden={control ? undefined : true}
    >
      <rect
        ref={control?.registerRef}
        x={-PORT_HIT_SIZE / 2}
        y={-PORT_HIT_SIZE / 2}
        width={PORT_HIT_SIZE}
        height={PORT_HIT_SIZE}
        fill="transparent"
        data-port-role={portRole}
        cursor={cursor}
        onClick={onClick}
        {...portControlAttrs(control)}
      />
      <PortBadgeContainer />
      {seq != null && (
        // Counter-rotate so the number stays upright even though the square above rotates
        // with the frame — a rotated numeral would be unreadable. `aria-hidden` because the
        // control's own `aria-label` already carries the sequence number; without it the numeral
        // would surface as a stray "1" next to the button.
        <g transform={`rotate(${-rotationDeg})`} aria-hidden="true">
          <PortSeqNumeral seq={seq} />
        </g>
      )}
      <PortFocusRing visible={control?.focused ?? false} />
    </g>
  )
}
